import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Beaker, ShieldCheck, ChevronRight, Mail, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate(user.role === "admin" ? "/admin" : (user.onboarding_complete ? "/dashboard" : "/onboarding"), { replace: true });
  }, [loading, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const res = await api.post("/auth/login", form);
      setUser(res.data.user);
      toast.success(`Welcome back, ${res.data.user.name.split(" ")[0]}`);
      const dest = res.data.user.role === "admin" ? "/admin" : (res.data.user.onboarding_complete ? "/dashboard" : "/onboarding");
      navigate(dest, { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="login-page" className="min-h-[80vh] flex items-center justify-center bg-slate-50 px-6 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white border border-slate-200 rounded-md p-10">
          <div className="flex items-center gap-3 mb-6">
            <img src="/logo.webp" alt="" className="h-11 w-11 object-contain"/>
            <div>
              <div className="font-display text-base font-semibold tracking-tight text-[#0e2f6b]">Peps<span className="text-blue-500">4</span>Pets</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-blue-700">Sign in · Customer Portal</div>
            </div>
          </div>
          <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">Welcome back.</h1>
          <p className="mt-2 text-slate-600 text-sm">Sign in with email or Google.</p>

          <form onSubmit={submit} className="mt-6 space-y-3" data-testid="email-login-form">
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.75}/>
              <Input
                data-testid="login-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@lab.com"
                className="h-11 pl-9 rounded-md border-slate-300"
              />
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.75}/>
              <Input
                data-testid="login-password"
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Password"
                className="h-11 pl-9 rounded-md border-slate-300"
              />
            </div>
            {err && <div data-testid="login-error" className="text-xs text-red-600 border border-red-200 bg-red-50 rounded-md p-2">{err}</div>}
            <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-md">
              {busy ? "Signing in…" : "Sign in"}
              <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.75} />
            </Button>
            <div className="flex items-center justify-between text-xs">
              <Link to="/auth/forgot-password" className="text-slate-600 hover:text-blue-700">Forgot password?</Link>
              <Link to="/auth/register" className="text-blue-700 hover:text-blue-800 font-medium" data-testid="link-register">Create account →</Link>
            </div>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">or</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <Button
            data-testid="google-login-btn"
            onClick={google}
            variant="outline"
            className="w-full h-11 border-slate-300 text-slate-800 rounded-md"
          >
            Continue with Google
          </Button>

          <div className="mt-8 flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            <ShieldCheck className="w-4 h-4 mt-0.5 text-blue-600" strokeWidth={1.75} />
            <span>Encrypted session · No password reuse across accounts.</span>
          </div>
        </div>
        <p className="mt-6 text-center text-xs text-slate-400 font-mono uppercase tracking-widest">Research access · 21+</p>
      </div>
    </div>
  );
}
